<script setup>
import { ref, watch } from "vue";
import { computed } from "vue";

import * as XLSX from "xlsx";
import Swal from "sweetalert2";

import { sendDataToServer } from "@/services/apiServices";

import { useTheme } from '@/composables/useThemes'
const { isDarkTheme } = useTheme()

const fileInput = ref(null);
const files = ref([]);
const parsedData = ref([]);
const headers = ref([]);
const hasInvalidFile = ref(false);
// const missingHeaders = ref([]);
const invalidRowIndexes = ref([]);
const invalidColumns = ref([]);
const invalidCells = ref(new Set());

const showTable = computed(() => parsedData.value.length > 0);


const invalidHeaders = computed(() => {
  const headerSet = new Set();
  for (const cell of invalidCells.value) {
    const [, columnName] = cell.split('-', 2); // format: "rowIndex-columnName"
    headerSet.add(columnName);
  }
  return headerSet;
});


const triggerFileInput = () => {
  fileInput.value.click();
};

const handleFileUpload = (event) => {
  const newFiles = event.target.files || event.dataTransfer.files;
  if (newFiles.length > 0) {
    Array.from(newFiles).forEach((file) => {
      if (validateFileExtension(file)) {
        parseExcelFile(file);
      } else {
        alert("Invalid file format. Please upload .xlsx or .xls files.");
      }
    });
  }
};

const validateFileExtension = (file) => {
  const validExtensions = [".xlsx", ".xls"];
  const fileExtension = file.name.split(".").pop().toLowerCase();
  return validExtensions.includes(`.${fileExtension}`); // Check if the file extension is valid
};

const validateHeadersAndData = (data, fileName) => {
  const headers = data[0];
  const requiredHeaders = [
    "Roll No",
    "Student Name",
    "Class Name",
    "Date of Birth",
    "Student Address",
    "Student Contact",
    "Valid Date",
    // "Student Gender",
    "Parent Name",
  ];
  const missingHeaders = requiredHeaders.filter(
    (header) => !headers.includes(header)
  );
  missingHeaders.value = missingHeaders;
  if (missingHeaders.length > 0) {
    Swal.fire({
      icon: "error",
      title: "Upload failed!",
      html: `File: <strong>${fileName}</strong> has not the same headers.<br>Headers missing: <strong>${missingHeaders.join(
        ", "
      )}</strong>`,
      confirmButtonColor: "#d33",
    });
    return false;
  }
  return true;
};

const parseExcelFile = (file) => {
  hasInvalidFile.value = false;
  invalidCells.value.clear();
  invalidRowIndexes.length = 0;
  const reader = new FileReader();

  reader.onload = (e) => {
    const data = new Uint8Array(e.target.result);
    parsedData.value = [];
    hasInvalidFile.value = false;
    const workbook = XLSX.read(data, {
      type: "array",
      dateNF: 'mm/dd/yyyy',
      cellDates: true,
    });

    const worksheet = workbook.Sheets[workbook.SheetNames[0]];
    const rawData = XLSX.utils.sheet_to_json(worksheet, {
      header: 1,
      defval: "",
      date1904: false,
    });

    const jsonData = rawData
      .filter(row => row.some(cell => cell !== ""))
      .map(row => row.map(cell => {
        if (Object.prototype.toString.call(cell) === '[object Date]' && !isNaN(cell)) {
          return cell.toLocaleDateString('en-US');
        }
        return cell;
      }));

    if (validateHeadersAndData(jsonData, file.name)) {
      headers.value = jsonData[0].map(header => ({
        text: header,
        value: header,
      }));

      const headerIndexMap = Object.fromEntries(
        headers.value.map((h, i) => [h.value, i])
      );

      const validDateIndex = headerIndexMap["Valid Date"];
      const studentNameIndex = headerIndexMap["Student Name"];
      const classNameIndex = headerIndexMap["Class Name"];
      const contactIndex = headerIndexMap["Student Contact"];

      const isValidContact = (contact) => /^[0-9]{10}$/.test(String(contact).trim());

      const columnErrors = {
        "Valid Date": false,
        "Student Name": false,
        "Class Name": false,
        "Student Contact": false,
      }; for (const [i, row] of jsonData.slice(1).entries()) {
        const rowIndex = i;

        const validDate = row[validDateIndex];
        const studentName = row[studentNameIndex];
        const className = row[classNameIndex];
        const contact = row[contactIndex];

        if (!validDate) invalidCells.value.add(`${rowIndex}-Valid Date`);
        if (!studentName) invalidCells.value.add(`${rowIndex}-Student Name`);
        if (!className) invalidCells.value.add(`${rowIndex}-Class Name`);
        if (contact && !isValidContact(contact)) {
          invalidCells.value.add(`${rowIndex}-Student Contact`);
        }

        const rowData = row.reduce((acc, col, i) => {
          acc[headers.value[i].value] = col;
          return acc;
        }, {});
        parsedData.value.push(rowData);
      }

      invalidColumns.value = Object.entries(columnErrors)
        .filter(([_, hasError]) => hasError)
        .map(([col]) => col);

      if (invalidCells.value.size > 0) {
        const invalidColumns = Array.from(invalidHeaders.value);
        Swal.fire({
          icon: "error",
          title: "Upload failed!",
          html: `The following columns have invalid data: <b>${invalidColumns.join(', ')}</b>`,
          confirmButtonColor: "#d33",
        });
        hasInvalidFile.value = true;
        return;
      }

      if (!files.value.some((f) => f.name === file.name)) {
        files.value.push({ name: file.name, isValid: true, file });
      }
    }
  };

  reader.readAsArrayBuffer(file);
};




const removeFile = (index) => {
  const removedFile = files.value.splice(index, 1)[0];
  // console.log("Removed file:", removedFile);

  // Reset parsed data and reprocess remaining files
  parsedData.value = [];
  files.value.forEach((f) => parseExcelFile(f.file));

  // Hide table if no files left
  if (!files.value.length) {
    headers.value = [];
    showTable.value = false;
  }
};

const uploadFile = async () => {
  const formData = new FormData();

  files.value.forEach((fileObj) => {
    formData.append("file", fileObj.file);
  });

  try {
    await sendDataToServer(formData);
    // console.log("Files uploaded successfully");
    // for (let pair of formData.entries()) {
    //   console.log("while upload fie:", pair[0], pair[1]);
    // }

    setTimeout(() => {
      files.value = [];
      parsedData.value = [];
      headers.value = [];
    }, 1500);

    Swal.fire({
      icon: "success",
      title: "Student upload successfully",
      toast: true,
      position: "top-end",
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
      background: isDarkTheme.value ? '#333' : '#fff',
      color: isDarkTheme.value ? '#fff' : '#000',
      iconColor: isDarkTheme ? "#0f0" : "#28a745",
      didOpen: (toast) => {
        toast.onmouseenter = Swal.stopTimer;
        toast.onmouseleave = Swal.resumeTimer;
      },
    });
  } catch (error) {
    console.error("Upload failed", error);
    alert("Upload failed. Please try again.");
  }
};

const handleDownload = () => {
  const link = document.createElement("a");
  // link.href = `${window.location.origin}/src/assets/excel_templates/studentHeader.xlsx`;
  link.href = `${window.location.origin}/excel_templates/studentHeader.xlsx`;

  link.download = "Student_Sample_Meroprints.xlsx";
  link.click();
};
</script>

<template>
  <v-container>
    <v-row justify="space-between" align="center" class="header-area">
      <h2>Student Dashboard</h2>
      <div class="d-flex align-center">
        <v-btn class="mr-2" outlined color="primary" @click="triggerFileInput">
          <v-icon left>mdi-upload</v-icon>
          Upload Excel
        </v-btn>
        <input type="file" ref="fileInput" accept=".xlsx,.xls" style="display: none" @change="handleFileUpload"
          multiple />
        <v-btn class="mr-4" outlined color="success" @click="handleDownload">
          <v-icon left>mdi-download</v-icon>Download Sample</v-btn>
      </div>
    </v-row>

    <div v-if="parsedData.length" class="file-list">
      <v-row>
        <v-col v-for="(file, index) in files" :key="index" cols="12" sm="6" md="4">
          <v-card class="file-card position-relative pa-3 d-flex align-center">
            <v-btn icon @click="removeFile(index)" class="close-btn" color="red" size="x-small">
              <v-icon>mdi-close</v-icon>
            </v-btn>
            <v-card-title class="text-subtitle-1 flex-shrink-0">{{
              file.name
            }}</v-card-title>
          </v-card>
        </v-col>
      </v-row>
    </div>
    <div class="d-flex justify-end">
      <v-btn v-if="files.length > 0 && !hasInvalidFile" color="primary" @click="uploadFile" class="mt-4">
        Submit
      </v-btn>
    </div>

    <div v-if="parsedData.length && showTable" class="table-container">
      <table class="styled-table">
        <thead>
          <tr>
            <th v-for="header in headers" :key="header.value"
              :class="{ 'invalid-header': invalidHeaders.has(header.value) }">
              {{ header.text }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, rowIndex) in parsedData" :key="rowIndex">
            <td v-for="(value, key, colIndex) in row" :key="colIndex"
              :class="{ 'invalid-cell': invalidCells.has(`${rowIndex}-${key}`) }">
              {{ value }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

  </v-container>
</template>

<style scoped>
.table-container {
  margin-top: 20px;
  overflow-x: auto;
}

.styled-table {
  width: 100%;
  border-collapse: collapse;
  margin: 25px 0;
  font-size: 0.9em;
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
  min-width: 400px;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.15);
}

.styled-table thead tr {
  background-color: #009879;
  color: #ffffff;
  text-align: left;
}

.styled-table th,
.styled-table td {
  padding: 12px 15px;
}

.styled-table tbody tr {
  border-bottom: 1px solid #dddddd;
}

.styled-table tbody tr:nth-of-type(even) {
  background-color: #f3f3f3;
}

.styled-table tbody tr:last-of-type {
  border-bottom: 2px solid #009879;
}

.styled-table tbody tr.active-row {
  font-weight: bold;
  color: #009879;
}

.file-card {
  background-color: #f1f1f1;
  position: relative;
  overflow: visible;
  border-radius: 8px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  width: 100%;
  max-width: 280px;
  margin: auto;
}

.file-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 6px 15px rgba(0, 0, 0, 0.1);
}

.invalid-cell {
  background-color: #f3ecff1a;
  /* optional, visible highlight */
  font-weight: 500;
  color: #b30000;
  /* red text */
}

.invalid-header {
  background-color: #ffe6e6;
  color: #b30000;
  font-weight: bold;
}

.missing-header {
  color: red;
  font-weight: bold;
}


.close-btn {
  position: absolute;
  top: 5px;
  right: 5px;
  transform: translate(50%, -50%);
  width: 22px;
  height: 22px;
  min-width: 22px;
  padding: 0;
  z-index: 10;
  background-color: rgba(255, 255, 255, 0.8);
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: background-color 0.3s ease, transform 0.3s ease;
}

.close-btn:hover {
  background-color: #ff6b6b;
  transform: translate(50%, -50%) scale(1.1);
}

/* File Name Styling */
.v-card-title {
  font-size: 14px;
  font-weight: bold;
  color: #2c3e50;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: center;
  padding: 10px 0;
}

.file-card:hover .v-card-title {
  color: #f09999;
}
</style>
